import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, DollarSign, Car } from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <Card className="stat-card" data-testid={`stat-card-${title.toLowerCase().replace(/ /g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-2xl sm:text-3xl font-bold text-gray-900">{value}</div>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isRider = user?.role === 'rider';
  const isDriver = user?.role === 'driver';

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isRider ? 'Welcome' : 'Dashboard'}
          </h2>
          <p className="text-gray-600 mt-1">
            {isRider ? 'Book your next ride' : 'Track your ride income at a glance'}
          </p>
        </div>
        {!isRider && (
          <Button
            onClick={() => navigate('/rides/new')}
            className="btn-primary"
            data-testid="add-ride-button"
          >
            Add New Ride
          </Button>
        )}
        {isRider && (
          <Button
            onClick={() => navigate('/rider/request')}
            className="btn-primary"
            data-testid="request-ride-button"
          >
            Request a Ride
          </Button>
        )}
      </div>

      {/* Rider Dashboard */}
      {isRider && (
        <div className="space-y-6">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Need a ride?</h3>
                  <p className="text-blue-100">Book your Black Suburban in seconds</p>
                </div>
                <Car className="w-16 h-16 text-white/30" />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="Total Rides"
              value={stats?.this_week?.total_rides || 0}
              subtitle="Rides you've requested"
              icon={Car}
              color="bg-blue-500"
            />
            <StatCard
              title="This Week"
              value={stats?.this_week?.total_rides || 0}
              subtitle="Rides this week"
              icon={Calendar}
              color="bg-purple-500"
            />
            <StatCard
              title="Total Spent"
              value={`$${(stats?.this_month?.total_earnings || 0).toFixed(2)}`}
              subtitle="This month"
              icon={DollarSign}
              color="bg-green-500"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => navigate('/rider/request')}
                className="w-full"
                size="lg"
              >
                Request a Ride
              </Button>
              <Button
                onClick={() => navigate('/rides/list')}
                variant="outline"
                className="w-full"
                size="lg"
              >
                View My Rides
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Driver/Admin Dashboard */}
      {!isRider && (
        <>
          {/* Available Rides Alert for Drivers */}
          {stats?.available_rides > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-900">
                      {stats.available_rides} Ride{stats.available_rides > 1 ? 's' : ''} Available
                    </h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Check out rides that need a driver
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/rides/available')}
                    variant="outline"
                    className="bg-white"
                    data-testid="view-available-rides-button"
                  >
                    View Available
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

      {/* This Week Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          This Week
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Total Rides"
            value={stats?.this_week?.total_rides || 0}
            subtitle={isAdmin ? "Rides created this week" : "Rides assigned to you"}
            icon={Car}
            color="bg-blue-500"
          />
          <StatCard
            title={isAdmin ? "Commission Earned" : "Net Earnings"}
            value={`$${(isAdmin ? stats?.this_week?.commission_earned || 0 : stats?.this_week?.net_earnings || 0).toFixed(2)}`}
            subtitle={isAdmin ? "20% from created rides" : "80% from assigned rides"}
            icon={DollarSign}
            color="bg-green-500"
          />
        </div>
        {!isAdmin && stats?.this_week?.commission_earned > 0 && (
          <div className="mt-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="text-sm">
                  <span className="font-semibold text-blue-900">Bonus: </span>
                  <span className="text-blue-700">${stats.this_week.commission_earned.toFixed(2)} commission from rides you created for others</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* This Month Stats */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          This Month
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Total Rides"
            value={stats?.this_month?.total_rides || 0}
            subtitle={isAdmin ? "Rides created this month" : "Rides assigned to you"}
            icon={Car}
            color="bg-indigo-500"
          />
          <StatCard
            title={isAdmin ? "Commission Earned" : "Net Earnings"}
            value={`$${(isAdmin ? stats?.this_month?.commission_earned || 0 : stats?.this_month?.net_earnings || 0).toFixed(2)}`}
            subtitle={isAdmin ? "20% from created rides" : "80% from assigned rides"}
            icon={DollarSign}
            color="bg-emerald-500"
          />
        </div>
        {!isAdmin && stats?.this_month?.commission_earned > 0 && (
          <div className="mt-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="text-sm">
                  <span className="font-semibold text-blue-900">Bonus: </span>
                  <span className="text-blue-700">${stats.this_month.commission_earned.toFixed(2)} commission from rides you created for others</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate('/rides')}
            data-testid="view-all-rides-button"
          >
            <Car className="w-4 h-4 mr-2" />
            View All Rides
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate('/reports/weekly')}
            data-testid="weekly-report-button"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Weekly Report
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate('/reports/monthly')}
            data-testid="monthly-report-button"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Monthly Report
          </Button>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default Dashboard;
